/**
 * InlineWidget — an Inline ("inline-grid"): a one-row nested grid inside a
 * section.
 *
 * It is a grid like a section's, not a bespoke list: blocks drop into it
 * through the grid engine, so the grid library's own DragPlaceholder is drawn
 * at the size of the held block and the row reflows live. Its children are
 * ordinary blocks, selected, moved and resized by cells. What is Inline's own:
 *
 *  - a dashed outline in the editor (the published row has none), and an
 *    empty row says "Drop blocks here" — no buttons, no "+";
 *  - a press on a block belongs to that block, a press on the row itself
 *    belongs to the Inline (it selects and drags like any block of the
 *    section around it).
 *
 * Selection chrome is the grid's own telegraph on the Inline's item box. The
 * row's height, columns, minimum width and alignment are kept by inlineRows.
 */
import { useEffect, useRef } from "react";
import { useGridStore, Grid, InteractionLayer } from "@fredericrous/grid";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { radii, spacing } from "@duro-app/tokens/tokens/spacing.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { css, html } from "react-strict-dom";

import { useCanvasZoomValue } from "../canvasZoom.js";
import { makeRenderWidget } from "./renderWidget.js";
import type { WidgetProps } from "./types.js";
import { borders } from "@duro-app/tokens/tokens/borders.css";

const styles = css.create({
  root: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  // Editor-only affordance: the row's extent, as the mockup's dashed box.
  outline: {
    // holds-until: website-builder is on Duro 5 (branch chore/duro-5.1),
    // whose `borders` tokens close this gap (design-system.every-measure-is-
    // a-token); Duro 4.5, pinned here, has no border-width token.
    borderWidth: borders.hairline,
    borderStyle: "dashed",
    borderColor: colors.border,
    borderRadius: radii.sm,
  },
  empty: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingLeft: spacing.xs,
    paddingRight: spacing.xs,
    color: colors.textMuted,
    fontSize: typography.fontSizeSm,
    pointerEvents: "none",
  },
});

export function InlineWidget({ contentProps, itemId, gridId }: WidgetProps) {
  const canvasZoom = useCanvasZoomValue();
  const nestedGridId = contentProps?.["nestedGridId"] as string | undefined;
  const preview = contentProps?.["preview"] as boolean | undefined;
  const count = useGridStore((s) =>
    nestedGridId ? Object.keys(s.grids[nestedGridId]?.items ?? {}).length : 0,
  );

  // A press on the row itself selects the Inline. The nested grid stops the
  // click before it bubbles to this element, and an Inline has no header
  // strip to press (a section does), so without this it could not be
  // selected by pointer at all. Capture phase, so the grid cannot stop it;
  // the selection is set once the event is done, so the grid's own
  // background handling cannot clear it again. A press on a block is left
  // to the block.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // On the Inline's whole grid item, not just this element: the item
    // wrapper's own layers sit beside the widget and take the press too.
    const host = rootRef.current?.closest("[data-grid-item]");
    if (!(host instanceof HTMLElement) || preview) return;
    const onClick = (ev: MouseEvent) => {
      const block = (ev.target as Element | null)?.closest?.(
        "[data-grid-item]",
      );
      if (block && block !== host) return;
      setTimeout(() => useGridStore.getState().setSelection(gridId, [itemId]));
    };
    host.addEventListener("click", onClick, true);
    return () => host.removeEventListener("click", onClick, true);
  }, [preview, gridId, itemId]);

  if (!nestedGridId) return null;
  const renderItem = makeRenderWidget(nestedGridId, !!preview);

  return (
    <html.div
      ref={rootRef}
      style={[styles.root, !preview && styles.outline]}
      onPointerDown={(e: {
        target: EventTarget | null;
        currentTarget: EventTarget | null;
        stopPropagation: () => void;
      }) => {
        // A block's press stays the block's; the row's own press bubbles to
        // the Inline's grid item (select, drag).
        const block = (e.target as Element | null)?.closest?.(
          "[data-grid-item]",
        );
        const own = (e.currentTarget as Element | null)?.closest?.(
          "[data-grid-item]",
        );
        if (block && block !== own) e.stopPropagation();
      }}
    >
      {preview ? (
        <Grid gridId={nestedGridId} preview={true} renderItem={renderItem} />
      ) : (
        <InteractionLayer>
          <Grid
            gridId={nestedGridId}
            preview={false}
            renderItem={renderItem}
            visualScale={canvasZoom}
          />
        </InteractionLayer>
      )}
      {!preview && count === 0 && (
        <html.div style={styles.empty}>Drop blocks here</html.div>
      )}
    </html.div>
  );
}
